import { DeleteOutline, MoreVert, NoteAddOutlined } from "@mui/icons-material";
import {
	IconButton,
	ListItemIcon,
	Menu,
	MenuItem,
	Paper,
	Stack,
	Typography,
} from "@mui/material";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";

import { useDeleteContractMutation } from "AdvisoryManagement/Api/Mutations/useDeleteContractMutation";
import { useUpdateContractMutation } from "AdvisoryManagement/Api/Mutations/useUpdateContractMutation";
import { useProductCategoriesQuery } from "AdvisoryManagement/Api/Queries/useProductCategoriesQuery";
import { AddContractSidebar } from "AdvisoryManagement/Components/Client/Contracts/AddContract";
import { ExtranetsDialog } from "AdvisoryManagement/Components/Extranets/ExtranetsDialog";
import { StatusChip } from "AdvisoryManagement/Shared/Status/StatusChip";
import { advisoryToolsExpectedCodes } from "AdvisoryManagement/Stores/constants";
import type { DummyExtendedContract } from "AdvisoryManagement/types";
import {
	ContractStatus,
	type CreateContractRequest,
	type ProductType,
	type ToolResponse,
} from "Infrastructure/Api/Api";
import { AppRouting } from "Infrastructure/Utils/UrlUtils";
import { DragHandle } from "Shared/DragDrop/SortableItem";

type ProposedSolutionRowProps = {
	solution: DummyExtendedContract;
	tools: ToolResponse[] | undefined;
};

export const ProposedSolutionRow = ({
	solution,
	tools,
}: ProposedSolutionRowProps) => {
	const productCategories = useProductCategoriesQuery();

	const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [extranetsDialogOpen, setExtranetsDialogOpen] = useState(false);
	const open = Boolean(menuAnchorEl);

	const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
		setMenuAnchorEl(event.currentTarget);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
	};

	const { mutateAsync: deleteProposedSolution } = useDeleteContractMutation();

	const handleDelete = () => {
		deleteProposedSolution(solution.contractId);
	};

	const categoryName =
		productCategories.data?.find((c) => c.code === solution.categoryCode)
			?.name || solution.categoryCode;

	const extendedTools = tools
		?.map((t) => getExtendedToolsInformation(t))
		.sort((a, b) => a.order - b.order);

	return (
		<Paper
			elevation={0}
			sx={{
				p: 2,
				backgroundColor: "#FFFFFF",
				border: "1px solid #E9EAEB",
				borderRadius: 1,
				"&:hover": {
					backgroundColor: "#FAFAFA",
				},
			}}
		>
			<Stack direction="row" alignItems="center" spacing={2}>
				{/* Drag Handle */}
				<DragHandle />

				{/* Category Name */}
				<Typography variant="body1" sx={{ flexGrow: 1 }}>
					{categoryName}
				</Typography>

				{/* Status Chips */}
				<Stack
					direction="row"
					spacing={0.5}
					alignItems="center"
					sx={{ flexShrink: 0 }}
				>
					{extendedTools?.map((tool) => (
						<StatusChip
							key={tool.code}
							label={tool.name}
							sx={{ cursor: "pointer" }}
							{...(tool.link
								? {
										component: Link,
										to: tool.link,
										target: "_blank",
										rel: "noopener",
									}
								: {})}
							{...(tool.isExtranets
								? {
										onClick: () => setExtranetsDialogOpen(true),
									}
								: {})}
						/>
					))}
				</Stack>

				{/* Actions */}
				<Stack direction="row" alignItems="center" sx={{ flexShrink: 0 }}>
					<CreateContractFromProposedSolution
						contractId={solution.contractId}
						categoryCode={solution.categoryCode}
						onOpenSidebar={() => setSidebarOpen(true)}
					/>
					<IconButton size="small" onClick={handleOpenMenu}>
						<MoreVert />
					</IconButton>
					<Menu anchorEl={menuAnchorEl} open={open} onClose={handleCloseMenu}>
						<CreateContractFromProposedSolution
							contractId={solution.contractId}
							categoryCode={solution.categoryCode}
							onOpenSidebar={() => {
								setSidebarOpen(true);
								handleCloseMenu();
							}}
							isMenuItem
						/>
						<MenuItem onClick={handleDelete}>
							<ListItemIcon>
								<DeleteOutline fontSize="small" color="error" />
							</ListItemIcon>
							Odebrat
						</MenuItem>
					</Menu>
				</Stack>
			</Stack>

			{/* Extranets Dialog */}
			<ExtranetsDialog
				open={extranetsDialogOpen}
				onClose={() => setExtranetsDialogOpen(false)}
				categoryCode={solution.categoryCode}
			/>

			{/* Sidebar rendered outside menu hierarchy */}
			{sidebarOpen && (
				<CreateContractSidebar
					contractId={solution.contractId}
					categoryCode={solution.categoryCode}
					onClose={() => setSidebarOpen(false)}
				/>
			)}
		</Paper>
	);
};

type CreateContractFromProposedSolutionProps = {
	contractId: string;
	categoryCode: ProductType | string | null | undefined;
	onOpenSidebar: () => void;
	isMenuItem?: boolean;
};

const CreateContractFromProposedSolution = ({
	onOpenSidebar,
	isMenuItem,
}: CreateContractFromProposedSolutionProps) => {
	if (isMenuItem) {
		return (
			<MenuItem onClick={onOpenSidebar}>
				<ListItemIcon>
					<NoteAddOutlined fontSize="small" color="action" />
				</ListItemIcon>
				Převést na smlouvu
			</MenuItem>
		);
	}

	return (
		<IconButton onClick={onOpenSidebar} title="Převést na smlouvu">
			<NoteAddOutlined color="action" fontSize="small" />
		</IconButton>
	);
};

type CreateContractSidebarProps = {
	contractId: string;
	categoryCode: ProductType | string | null | undefined;
	onClose: () => void;
};

const CreateContractSidebar = ({
	contractId,
	categoryCode,
	onClose,
}: CreateContractSidebarProps) => {
	const { advisoryCaseId } = useParams<{ advisoryCaseId: string }>();
	const { mutateAsync: updateContract } = useUpdateContractMutation();

	const handleCreateContract = async (data: CreateContractRequest) => {
		try {
			await updateContract({ ...data, contractId });
			onClose();
			toast.success("Smlouva byla vytvořena.");
		} catch (error) {
			toast.error("Nastala chyba při vytváření smlouvy.");
			console.error(error);
		}
	};

	return (
		<AddContractSidebar
			onClose={onClose}
			onSubmit={handleCreateContract}
			initialValues={{
				advisoryCaseId: advisoryCaseId ?? "",
				status: ContractStatus.InProgress,
			}}
			presetMainCategoryCode={categoryCode ?? undefined}
		/>
	);
};

type ExtendedTool = ToolResponse & {
	link?: string;
	isExtranets?: boolean;
};

const getExtendedToolsInformation = (tool: ToolResponse) => {
	const extendedTool: ExtendedTool = {
		...tool,
		isExtranets: tool.code === advisoryToolsExpectedCodes.extranets,
	};

	if (tool.code === advisoryToolsExpectedCodes.finam) {
		extendedTool.link = AppRouting.External.Finam.path();
	} else if (tool.code === advisoryToolsExpectedCodes.elis) {
		extendedTool.link = AppRouting.External.Elis.path();
	} else if (tool.code === advisoryToolsExpectedCodes.zzj) {
		extendedTool.link = AppRouting.External.Zzj.path();
	} else if (tool.code === advisoryToolsExpectedCodes.investmentQuestionnaire) {
		extendedTool.link = AppRouting.External.InvestmentQuestionnaire.path();
	}

	return extendedTool;
};
